'use server';

import { AuthError } from 'next-auth';
import bcrypt from 'bcryptjs';
import { eq } from 'drizzle-orm';
import QRCode from 'qrcode';
import { db, users } from '@nmedia/db';
import { signIn } from '@/auth';
import { generateTotpSecret, otpauthUrl, verifyTotp } from '@/lib/totp';
import {
  encodeTotpSecret,
  decodeTotpSecret,
  generateBackupCodes,
  hashBackupCodes,
} from '@/lib/twofactor';
import { logAudit } from '@/lib/audit';

export type PrecheckResult =
  | { status: 'invalid' }
  | { status: 'totp' }
  | { status: 'enroll'; email: string; secret: string; otpauthUrl: string; qrDataUrl: string };

/**
 * Étape 1 : vérifie email + mot de passe. Indique si l'utilisateur doit
 * s'enrôler (pas encore de 2FA) ou saisir son code TOTP.
 * Ne crée aucune session ; la connexion réelle se fait via finishLogin.
 */
export async function precheckLogin(
  emailRaw: string,
  password: string,
): Promise<PrecheckResult> {
  const email = emailRaw.trim().toLowerCase();
  if (!email || !password) return { status: 'invalid' };

  const u = (await db.select().from(users).where(eq(users.email, email)).limit(1))[0];
  if (!u || !u.isActive) return { status: 'invalid' };
  const ok = await bcrypt.compare(password, u.passwordHash);
  if (!ok) return { status: 'invalid' };

  if (u.totpEnabled && decodeTotpSecret(u.totpSecret)) {
    return { status: 'totp' };
  }

  // Pas de 2FA active → secret STABLE : on réutilise le secret en attente s'il
  // existe déjà, sinon on en génère un et on le persiste (totp_enabled reste
  // false). Indispensable pour que la clé ne change pas entre deux tentatives
  // (sinon le code de l'app d'authentification ne correspond jamais).
  let secret = decodeTotpSecret(u.totpSecret);
  if (!secret) {
    secret = generateTotpSecret();
    await db.update(users).set({ totpSecret: encodeTotpSecret(secret) }).where(eq(users.id, u.id));
  }
  const url = otpauthUrl(u.email, secret);
  const qrDataUrl = await QRCode.toDataURL(url, { margin: 1, width: 220 });
  return { status: 'enroll', email: u.email, secret, otpauthUrl: url, qrDataUrl };
}

export type EnrollResult =
  | { ok: true; backupCodes: string[] }
  | { ok: false; error: string };

/**
 * Étape 2 (enrôlement) : confirme un code TOTP pour le secret proposé, puis
 * active la 2FA et génère des codes de secours (affichés une seule fois).
 */
export async function enrollConfirm(
  emailRaw: string,
  password: string,
  secret: string,
  code: string,
): Promise<EnrollResult> {
  const email = emailRaw.trim().toLowerCase();
  const u = (await db.select().from(users).where(eq(users.email, email)).limit(1))[0];
  if (!u || !u.isActive) return { ok: false, error: 'Identifiants invalides.' };
  const ok = await bcrypt.compare(password, u.passwordHash);
  if (!ok) return { ok: false, error: 'Identifiants invalides.' };

  // On vérifie contre le secret en attente RÉELLEMENT stocké (source de vérité),
  // avec repli sur celui transmis par le client si rien en base.
  const effective = decodeTotpSecret(u.totpSecret) ?? secret;
  if (!verifyTotp(effective, code)) {
    return { ok: false, error: 'Code incorrect. Vérifie l’heure de ton téléphone.' };
  }

  const backupCodes = generateBackupCodes(8);
  const hashed = await hashBackupCodes(backupCodes);
  await db
    .update(users)
    .set({
      totpSecret: encodeTotpSecret(effective),
      totpEnabled: true,
      totpBackupCodes: hashed,
    })
    .where(eq(users.id, u.id));

  await logAudit({
    organizationId: u.organizationId ?? undefined,
    actorType: 'user',
    actorId: u.id,
    action: 'user.2fa_enroll',
    targetType: 'user',
    targetId: u.id,
  });

  return { ok: true, backupCodes };
}

/**
 * Étape finale : ouvre la session (signIn) avec email + mot de passe + code.
 * Redirige vers / en cas de succès, renvoie une erreur sinon.
 */
export async function finishLogin(
  emailRaw: string,
  password: string,
  code: string,
): Promise<{ error: string } | void> {
  const email = emailRaw.trim().toLowerCase();
  try {
    await signIn('credentials', { email, password, code, redirectTo: '/' });
  } catch (err) {
    if (err instanceof AuthError) {
      return { error: 'Code de vérification invalide.' };
    }
    throw err; // NEXT_REDIRECT (succès) → à propager
  }
}
